import { useEffect, useState } from "react";
import { Mail, RotateCw, X, AlertCircle, CheckCircle2, Clock, FileWarning } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LEAD_STATUSES, getStatusInfo, formatDateTime } from "../lib/adminConstants";

export default function LeadDetailModal({ lead, open, onOpenChange, onUpdated }) {
    const [notes, setNotes] = useState("");
    const [resending, setResending] = useState(false);

    useEffect(() => {
        setNotes(lead?.notes || "");
    }, [lead]);

    if (!open || !lead) return null;

    const statusInfo = getStatusInfo(LEAD_STATUSES, lead.email_status || "pending");

    const StatusIcon = {
        sent: CheckCircle2,
        failed: AlertCircle,
        pending: Clock,
        no_pdf: FileWarning,
    }[lead.email_status || "pending"] || Clock;

    const saveNotes = async () => {
        try {
            await api.patch(`/admin/leads/${lead.id}`, { notes });
            toast.success("Notiz gespeichert");
            onUpdated?.({ ...lead, notes });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const resend = async () => {
        setResending(true);
        try {
            const res = await api.post(`/admin/leads/${lead.id}/resend`);
            toast.success("E-Mail erneut gesendet");
            onUpdated?.({
                ...lead,
                email_status: res.data.email_status,
                email_error: "",
                email_sent_at: new Date().toISOString(),
                resend_id: res.data.resend_id || "",
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            // Refresh lead state on failure too
            onUpdated?.({
                ...lead,
                email_status: "failed",
                email_error: err.response?.data?.detail || "Versand fehlgeschlagen",
            });
        } finally {
            setResending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6"
            onClick={() => onOpenChange(false)}
            data-testid="lead-detail-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-2xl rounded-t-3xl lg:rounded-3xl max-h-[90vh] overflow-y-auto"
            >
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-6 border-b border-black/5">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold">Lead</div>
                        <h2 className="font-display text-2xl font-bold truncate">{lead.name || "—"}</h2>
                        <div className="text-sm text-aimploy-muted truncate">{lead.email}</div>
                    </div>
                    <button
                        onClick={() => onOpenChange(false)}
                        className="p-2 rounded-lg hover:bg-aimploy-bg flex-shrink-0"
                        aria-label="Schließen"
                        data-testid="lead-detail-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Status banner */}
                    <div className={`rounded-2xl p-5 ${statusInfo.color}`}>
                        <div className="flex items-start gap-3">
                            <StatusIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold">{statusInfo.label}</div>
                                {lead.email_sent_at && lead.email_status === "sent" && (
                                    <div className="text-xs mt-1 opacity-80">Gesendet: {formatDateTime(lead.email_sent_at)}</div>
                                )}
                                {lead.email_error && (
                                    <div className="text-xs mt-1 opacity-90 break-words">{lead.email_error}</div>
                                )}
                                {lead.resend_id && (
                                    <div className="text-[11px] mt-1 opacity-70 font-mono break-all">Resend ID: {lead.resend_id}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Meta */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-1">Quelle</div>
                            <div className="font-medium">{lead.source || "—"}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-1">Erfasst am</div>
                            <div className="font-medium">{formatDateTime(lead.created_at)}</div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={resend}
                            disabled={resending}
                            data-testid="lead-resend-mail"
                            className="inline-flex items-center gap-2 bg-aimploy-violet hover:bg-aimploy-violet-hover disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                        >
                            <RotateCw className={`w-4 h-4 ${resending ? "animate-spin" : ""}`} />
                            {resending ? "Sende..." : "Mail erneut senden"}
                        </button>
                        <a
                            href={`mailto:${lead.email}`}
                            className="inline-flex items-center gap-2 border border-black/10 hover:bg-aimploy-bg text-aimploy-navy px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                        >
                            <Mail className="w-4 h-4" /> Persönlich antworten
                        </a>
                    </div>

                    {/* Notes */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Interne Notiz</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            data-testid="lead-detail-notes"
                            placeholder="Notiz hinzufügen…"
                            className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm resize-y"
                        />
                        <button
                            onClick={saveNotes}
                            data-testid="lead-detail-save-notes"
                            className="mt-2 inline-flex items-center gap-2 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-4 py-2 rounded-full text-xs font-semibold transition-colors"
                        >
                            Notiz speichern
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
